import L from "leaflet";
import * as turf from "@turf/turf";
import { Position } from "geojson";
import { ProjectionsRegistrar } from "@shared/mirage/utils/ProjectionRegistrar";
import { extend } from "lodash";
import { IMapViewPallette } from "@shared/mirage/core/contracts/IMapView";
import { GaiaMapTheme } from "../gaiaMap/hooks/useGaiaMapTheme";

export type StylingLayerType =
    | "polygon"
    | "polyline"
    | "drawing"
    | "marker"
    | "vertex-marker"
    | "first-vertex-marker"
    | "drawing-polygon"
    | "drawing-circle"
    | "drawing-vertex-marker"
    | "drawing-first-vertex-marker"
    | "ruler-line"
    | "ruler-vertex-marker"
    | "ruler-first-vertex-marker"
    | "ruler-polygon";

export interface ColorPalette {
    editedGeometry?: string;
    editedGeometryFill?: string;
    ruler?: string;
    rulerFill?: string;
    geometry?: string;
    geometryFill?: string;
}

export class MapViewHelper {
    private static colorPalette: ColorPalette | null = null;

    public static convertColorPaletteToGaiaTheme(pallette: IMapViewPallette): GaiaMapTheme {
        const theme: GaiaMapTheme = {
            palette: {
                editedGeometry: pallette.editedGeometryColor || undefined,
                editedGeometryFill: pallette.editedGeometryFillColor || undefined,
                ruler: pallette.rulerColor || undefined,
                rulerFill: pallette.rulerFillColor || undefined,
                geometry: pallette.geometryColor || undefined,
                geometryFill: pallette.geometryFillColor || undefined,
            },
        };
        return theme;
    }

    public static setColorPalette(palette: ColorPalette | null) {
        this.colorPalette = palette;
    }

    public static getDefaultStyle(layerType: StylingLayerType) {
        switch (layerType) {
            case "polygon":
                return {
                    color: "#ff0000",
                    fillColor: "rgba(255, 51, 51, 0.6)",
                    weight: 2,
                };
            case "drawing-polygon":
                return {
                    color: "#0066ff",
                    fillColor: "rgba(51, 153, 255, 0.6)",
                    weight: 2,
                };
            case "drawing-circle":
                return {
                    color: "#0066ff",
                    fillColor: "rgba(51, 153, 255, 0.6)",
                    weight: 2,
                };
            case "polyline":
                return {
                    color: "#ff0000",
                    weight: 2,
                };
            case "drawing":
                return {
                    color: "#0066ff",
                    weight: 2,
                    dashArray: "5,5",
                    opacity: 0.7,
                };
            case "drawing-vertex-marker":
                return {
                    color: "#0066ff",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 1,
                    radius: 3,
                };
            case "drawing-first-vertex-marker":
                return {
                    color: "#0066ff",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 2,
                    radius: 7,
                };
            case "vertex-marker":
                return {
                    color: "#ff0000",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 1,
                    radius: 3,
                };
            case "first-vertex-marker":
                return {
                    color: "#ff0000",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 2,
                    radius: 7,
                };
            case "ruler-line":
                return {
                    color: "#ff69b4",
                    weight: 4,
                };
            case "ruler-vertex-marker":
                return {
                    color: "#ff69b4",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 1,
                    radius: 3,
                };
            case "ruler-first-vertex-marker":
                return {
                    color: "#ff69b4",
                    fillColor: "rgba(255, 255, 255, 1)",
                    weight: 2,
                    radius: 7,
                };
            case "ruler-polygon":
                return {
                    color: "#ff69b4",
                    fillColor: "rgba(255, 182, 193, 0.6)",
                    weight: 4,
                };
            default:
                return {
                    color: "#ff0000",
                    weight: 2,
                };
        }
    }

    public static getLayerStyling(layerType: StylingLayerType) {
        const defaultStyle = this.getDefaultStyle(layerType);

        if (!this.colorPalette) {
            return defaultStyle;
        }

        let color: string | undefined;
        let fillColor: string | undefined;

        // Map the layer type to the appropriate color palette keys
        if (layerType.includes("ruler") || layerType === "ruler-vertex-marker" || layerType === "ruler-first-vertex-marker") {
            color = this.colorPalette.ruler;
            fillColor = this.colorPalette.rulerFill;
        } else if (layerType.includes("drawing") || layerType === "vertex-marker" || layerType === "first-vertex-marker") {
            color = this.colorPalette.editedGeometry;
            fillColor = this.colorPalette.editedGeometryFill;
        } else {
            color = this.colorPalette.geometry;
            fillColor = this.colorPalette.geometryFill;
        }

        // Create override style object with only defined values
        const overrideStyle: Record<string, any> = {};
        if (color !== undefined) {
            overrideStyle.color = color;
        }
        if (fillColor !== undefined && layerType.includes("polygon")) {
            overrideStyle.fillColor = fillColor;
        }

        return { ...defaultStyle, ...overrideStyle };
    }

    static GetCenterXYByPoints(coordinates: [number, number][]) {
        const centerFeature = turf.center(turf.points(coordinates));
        return this.turfFeatureToXY(centerFeature);
    }
    static GetCenterXYByGeometry(geometry: GeoJSON.Geometry): [number, number] {
        const allPoints: [number, number][] = [];
        if (geometry.type === "MultiPolygon") {
            const multipolygon = geometry as GeoJSON.MultiPolygon;
            for (const coords of multipolygon.coordinates) {
                for (const points of coords) {
                    allPoints.push(...points.map(this.positionToXY));
                }
            }
        }
        if (geometry.type === "Polygon") {
            const multipolygon = geometry as GeoJSON.Polygon;
            for (const point of multipolygon.coordinates[0]) {
                allPoints.push(this.positionToXY(point));
            }
        }
        if (geometry.type === "Point") {
            const point = geometry as GeoJSON.Point;
            return this.positionToXY(point.coordinates);
        }
        return this.turfFeatureToXY(turf.center(turf.points(allPoints)));
    }
    static positionToXY(point: Position): [number, number] {
        return [point[1], point[0]];
    }
    static turfFeatureToXY(f: turf.helpers.Feature<turf.helpers.Point, turf.helpers.Properties>): [number, number] {
        const centerCoordinates = f.geometry.coordinates;
        return [centerCoordinates[0], centerCoordinates[1]] as [number, number];
    }
    static pickFirstGeometry(geometry: GeoJSON.Geometry | GeoJSON.FeatureCollection): GeoJSON.Geometry | null {
        if ((geometry as GeoJSON.Polygon).coordinates) {
            return geometry as GeoJSON.Polygon;
        } else if (geometry.type) {
            return geometry as GeoJSON.Geometry;
        } else {
            const collection = geometry as GeoJSON.FeatureCollection;
            const feature = collection.features[0];
            if (!feature) {
                return null;
            }
            return feature.geometry;
        }
    }
    static IsGeoJsonEmpty(geometries: GeoJSON.GeoJSON | undefined | null) {
        if (!geometries) {
            return true;
        }

        const featureCollection = geometries as GeoJSON.FeatureCollection;
        if (typeof featureCollection.features === "undefined") {
            return typeof (geometries as any).coordinates == "undefined";
        }

        return featureCollection.features.length === 0;
    }
    public static setEmptyTexts() {
        L.drawLocal.draw.toolbar.buttons.polygon = "";
        L.drawLocal.edit.toolbar.buttons.edit = "";
        L.drawLocal.edit.toolbar.buttons.remove = "";
    }
    public static getEditPolygonsToolBar(drawnItems: L.FeatureGroup<any>) {
        return new L.Control.Draw({
            position: "topright",
            draw: {
                polygon: {
                    allowIntersection: false,
                    drawError: {
                        color: "#e1e100",
                        message: "Unable to draw self intersecting polygons",
                    },
                    shapeOptions: {
                        color: "#97009c",
                    },
                },
                polyline: false,
                circle: false,
                rectangle: false,
                marker: false,
                circlemarker: false,
            },
            edit: {
                featureGroup: drawnItems,
                remove: true,
            },
        });
    }
    public static GetDefaultCRS() {
        const name = ProjectionsRegistrar.GetEPSG27700Name();
        const proj = ProjectionsRegistrar.GetEPSG27700Definition();
        return this.GetCRS(name, proj);
    }
    private static GetCRS(projName: string, projDefinition: string, options?: L.Proj.ProjCRSOptions) {
        const crsOption = extend(options, {
            resolutions: [896, 448, 224, 112, 56, 28, 14, 7, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375, 0.109375 / 2, 0.109375 / 4],
            origin: [-238375, 1376256],
        });
        return new window.L.Proj.CRS(projName, projDefinition, crsOption);
    }
    public static getDisabledPolygonsToolBar(drawnItems: L.FeatureGroup<any>) {
        return new L.Control.Draw({
            position: "topright",
            draw: {
                polygon: false,
                polyline: false,
                circle: false,
                rectangle: false,
                marker: false,
                circlemarker: false,
            },
            edit: {
                featureGroup: drawnItems,
                remove: true,
            },
        });
    }
}
